/**
 * MyFeedbackModal — "My reports": a read-only, paginated list of the current user's own
 * past feedback submissions.
 *
 * Deliberately a SEPARATE modal from FeedbackModal (the submission form) — different store flag
 * (`isMyReportsOpen`), different purpose (review, not compose). Card-list layout rather than
 * FeedbackTab.tsx's admin `Table` because a table forces horizontal scroll on mobile; cards stack.
 */
import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, Loader2, MousePointerClick } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { listMyFeedback, type MyFeedbackRow } from '@/api/feedback'
import { useFeedbackStore, selectIsMyReportsOpen } from '@/stores/useFeedbackStore'

const PAGE_SIZE = 10

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  submitted: 'default',
  mock: 'secondary',
  queued: 'outline',
  failed: 'destructive',
}

const TYPE_LABEL: Record<string, string> = {
  bug: 'Bug',
  data_integrity: 'Data integrity',
  suggestion: 'Suggestion',
  feature: 'Feature',
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function ReportCard({ row }: { row: MyFeedbackRow }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{TYPE_LABEL[row.feedback_type] ?? row.feedback_type}</Badge>
        <Badge variant={STATUS_VARIANT[row.status] ?? 'outline'}>{row.status}</Badge>
        {row.has_annotation && (
          <MousePointerClick className="text-muted-foreground size-3.5" aria-label="Annotated" />
        )}
        <span className="text-muted-foreground ml-auto text-xs">{formatDate(row.created_at)}</span>
      </div>
      <p className="mt-2 line-clamp-2 text-sm font-medium break-words">{row.title}</p>
      <p className="text-muted-foreground mt-1 line-clamp-2 text-sm break-words">
        {row.description}
      </p>
      {row.github_issue_number && (
        <a
          href={row.github_issue_url ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="text-primary mt-2 inline-flex items-center gap-1 text-xs hover:underline"
        >
          <ExternalLink className="size-3" />#{row.github_issue_number}
          {row.github_state && (
            <Badge variant={row.github_state === 'closed' ? 'secondary' : 'outline'}>
              {row.github_state}
            </Badge>
          )}
        </a>
      )}
    </div>
  )
}

export function MyFeedbackModal() {
  const open = useFeedbackStore(selectIsMyReportsOpen)
  const closeMyReports = useFeedbackStore((s) => s.closeMyReports)

  const [rows, setRows] = useState<MyFeedbackRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (pageOffset: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await listMyFeedback(PAGE_SIZE, pageOffset)
      setRows(res.data)
      setTotal(res.total)
    } catch {
      setError('Could not load your reports. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  // Reset to page 1 and (re)fetch every time the modal is opened.
  useEffect(() => {
    if (!open) return
    setOffset(0)
    load(0)
  }, [open, load])

  const handlePrevious = () => {
    const next = Math.max(0, offset - PAGE_SIZE)
    setOffset(next)
    load(next)
  }

  const handleNext = () => {
    const next = offset + PAGE_SIZE
    setOffset(next)
    load(next)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeMyReports()}>
      <DialogContent
        data-testid="my-reports-modal"
        className="flex max-h-[90vh] flex-col overflow-x-hidden overflow-y-auto sm:max-w-lg [&>*]:min-w-0"
      >
        <DialogHeader>
          <DialogTitle>My reports</DialogTitle>
          <DialogDescription>Feedback and issues you've submitted.</DialogDescription>
        </DialogHeader>

        {loading && rows.length === 0 && (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {!loading && error && <p className="text-destructive text-sm">{error}</p>}

        {!loading && !error && rows.length === 0 && (
          <p className="text-muted-foreground py-8 text-center text-sm">
            You haven't submitted any reports yet.
          </p>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map((row) => (
              <ReportCard key={row.id} row={row} />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t pt-3">
            <span className="text-muted-foreground text-xs">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || loading}
                onClick={handlePrevious}
              >
                {loading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ChevronLeft className="size-4" />
                )}
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= total || loading}
                onClick={handleNext}
              >
                Next
                {loading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
